import React, { useContext, lazy, Suspense } from 'react';
import classnames from 'classnames';
import { useIsMounted } from '@wix/da-hooks/pkg/useIsMounted';
import { NotificationQueueItem, NotificationRenderFunction } from './redux/types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import retry from '../utils/webpack/chunkRetry';

import s from './Notifications.scss';

const DesktopNotificationAnimator = lazy(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "notifs" */
        './_partials/DesktopNotificationAnimator'
      )
  )
);
const MobileNotificationAnimator = lazy(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "notifs" */
        './_partials/MobileNotificationAnimator'
      )
  )
);

export interface Props {
  queueName?: string;
  queue: NotificationQueueItem[];
  wrapperClassName?: string;
  animatorClassName?: string;
  /** Don't render other notification types and only use the render
   *  function defined as the Notifications children */
  onlyUseRenderFunction?: boolean;
  children?: NotificationRenderFunction;
  removeNotification: (id: number) => void;
}

const Notifications: React.FC<Props> = ({
  queue,
  wrapperClassName,
  animatorClassName,
  ...props
}) => {
  // react-spring leaks in ssr, so must avoid rendering it
  const mounted = useIsMounted();
  const isMobile = useContext(MobileContext);

  if (!mounted || !queue || queue.length === 0) {
    return null;
  }

  return (
    <div className={classnames(s['root'], wrapperClassName)}>
      <Suspense fallback={null}>
        {isMobile ? (
          <MobileNotificationAnimator
            queue={queue}
            notificationProps={props}
            className={animatorClassName}
          />
        ) : (
          <DesktopNotificationAnimator
            queue={queue}
            notificationProps={props}
            className={animatorClassName}
          />
        )}
      </Suspense>
    </div>
  );
};

export default Notifications;
